"use client";

import { useState, Suspense } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useAction } from "next-safe-action/hooks";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { AuthShell } from "@/components/auth/auth-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { PasswordInput } from "@/components/auth/password-input";
import { MfaInput } from "@/components/auth/mfa-input";
import { loginUser, getPostAuthRedirectPath } from "@/lib/actions/auth";
import { authClient } from "@/lib/auth/client";

function LoginContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [isMfaVerifying, setIsMfaVerifying] = useState(false);
  const [loginError, setLoginError] = useState("");

  const { execute: executeLogin, status: loginStatus, result: loginResult } = useAction(loginUser, {
    onSuccess: async (data) => {
      if (data.data?.success) {
        setLoginError("");
        if (data.data.requiresMfa) {
          setMfaRequired(true);
          toast.info("Please enter your MFA code");
        } else {
          toast.success("Logged in successfully!");
          // Use callback URL if provided, otherwise use routing logic
          if (callbackUrl) {
            window.location.href = callbackUrl;
          } else {
            const redirectResult = await getPostAuthRedirectPath({});
            window.location.href = redirectResult?.data?.redirectPath || "/dashboard";
          }
        }
      } else {
        // Login failed - show error
        const errorMsg = data.data?.error || "Invalid email or password";
        setLoginError(errorMsg);
        const attempts = failedAttempts + 1;
        setFailedAttempts(attempts);

        if (attempts >= 5) {
          setIsLocked(true);
        }

        toast.error(errorMsg, {
          description: attempts < 5 ? `${5 - attempts} attempt${5 - attempts !== 1 ? "s" : ""} remaining` : undefined,
          duration: 4000,
        });
      }
    },
    onError: (error) => {
      const attempts = failedAttempts + 1;
      setFailedAttempts(attempts);

      // Extract error message from server error
      const errorMessage = error?.error?.serverError || "Invalid email or password";

      if (attempts >= 5) {
        setIsLocked(true);
        toast.error("Account locked after multiple failed attempts", {
          description: "Check your email to unlock or wait 15 minutes. Contact support if you need help.",
          duration: 6000,
        });
      } else {
        toast.error(errorMessage, {
          description: `${5 - attempts} attempt${5 - attempts !== 1 ? "s" : ""} remaining before account lock`,
          duration: 4000,
        });
      }
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLocked) {
      toast.error("Account is locked. Please try again later.");
      return;
    }

    if (!email || !password) {
      setLoginError("Please enter your email and password");
      toast.error("Please enter your email and password");
      return;
    }

    setLoginError(""); // Clear previous errors
    executeLogin({ email, password, rememberMe });
  };

  const handleMfaComplete = async (code: string) => {
    if (isLocked) {
      toast.error("Account is locked. Please try again later.");
      return;
    }

    setIsMfaVerifying(true);

    try {
      const result = await authClient.twoFactor.verifyTotp({
        code,
        trustDevice: true,
      });

      if (result.data) {
        toast.success("MFA verified successfully!");
        // Use callback URL if provided, otherwise use routing logic
        if (callbackUrl) {
          window.location.href = callbackUrl;
        } else {
          const redirectResult = await getPostAuthRedirectPath({});
          window.location.href = redirectResult?.data?.redirectPath || "/dashboard";
        }
      } else {
        const attempts = failedAttempts + 1;
        setFailedAttempts(attempts);

        if (attempts >= 5) {
          setIsLocked(true);
          toast.error("Account locked after multiple failed MFA attempts", {
            description: "Wait 15 minutes before trying again or contact support.",
            duration: 6000,
          });
        } else {
          toast.error("Invalid MFA code", {
            description: `Make sure your device time is synced. ${5 - attempts} attempt${5 - attempts !== 1 ? "s" : ""} remaining.`,
            duration: 4000,
          });
        }
      }
    } catch (error) {
      const attempts = failedAttempts + 1;
      setFailedAttempts(attempts);

      if (attempts >= 5) {
        setIsLocked(true);
        toast.error("Account locked after multiple failed MFA attempts", {
          description: "Wait 15 minutes before trying again or contact support.",
          duration: 6000,
        });
      } else {
        toast.error("Invalid MFA code", {
          description: `Make sure your device time is synced. ${5 - attempts} attempt${5 - attempts !== 1 ? "s" : ""} remaining.`,
          duration: 4000,
        });
      }
    } finally {
      setIsMfaVerifying(false);
    }
  };

  const isLoading = loginStatus === "executing" || isMfaVerifying;

  return (
    <AuthShell
      title={mfaRequired ? "Enter MFA Code" : "Welcome back"}
      subtitle={
        mfaRequired
          ? "Please enter the 6-digit code from your authenticator app"
          : "Sign in to your account to continue"
      }
    >

      {!mfaRequired ? (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading || isLocked}
              autoComplete="email"
              required
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link
                href="/reset-password"
                className="text-sm text-accent hover:text-accent/90 transition-colors"
              >
                Forgot password?
              </Link>
            </div>
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading || isLocked}
              autoComplete="current-password"
              required
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="remember"
              checked={rememberMe}
              onCheckedChange={(checked) => setRememberMe(checked === true)}
              disabled={isLoading || isLocked}
            />
            <Label
              htmlFor="remember"
              className="text-sm font-normal cursor-pointer"
            >
              Remember me for 30 days
            </Label>
          </div>

          {/* Error Display */}
          {loginError && !isLocked && (
            <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
              {loginError}
            </div>
          )}

          {isLocked && (
            <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
              <p className="font-semibold">Account Temporarily Locked</p>
              <p className="mt-1 text-xs">Too many failed login attempts. Please try again in 15 minutes or reset your password.</p>
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading || isLocked}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Sign in
          </Button>
        </form>
      ) : (
        <div className="space-y-5">
          <MfaInput
            onComplete={handleMfaComplete}
            disabled={isLoading || isLocked}
          />

          {isLocked && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              Account locked due to too many failed MFA attempts. Please try again in 15 minutes.
            </div>
          )}

          <Button
            variant="ghost"
            className="w-full"
            onClick={() => {
              setMfaRequired(false);
              setFailedAttempts(0);
              setIsLocked(false);
            }}
            disabled={isLoading}
          >
            Back to login
          </Button>
        </div>
      )}

      <div className="mt-6 text-center text-sm">
        <span className="text-muted-foreground">Don't have an account? </span>
        <Link
          href="/register"
          className="font-medium text-accent hover:text-accent/90 transition-colors"
        >
          Sign up
        </Link>
      </div>
    </AuthShell>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <AuthShell
        title="Welcome back"
        subtitle="Sign in to your account to continue"
      >
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AuthShell>
    }>
      <LoginContent />
    </Suspense>
  );
}
